"use client";

import "@/lib/installDomReconciliationGuard";

import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { I18nextProvider } from "react-i18next";
import LanguageMismatchNotice from "@/components/common/LanguageMismatchNotice";
import SupportWidget from "@/components/SupportWidget";
import GailDockLayout from "@/components/chat/dock/GailDockLayout";
import BrandSyncFromOrg from "@/components/common/BrandSyncFromOrg";
import FeatureAnnouncementHost from "@/components/common/FeatureAnnouncementHost";
import { GoogleTagManagerHead } from "@/components/common/GoogleTagManager";
import { LayoutBodyScripts } from "@/components/common/LayoutBodyScripts";
import NotAuthorizedAlert from "@/components/common/NotAuthorizedAlert";
import ProviderComposer, { ProviderConfig } from "@/components/common/ProviderComposer";
import ReleaseNotesWidget from "@/components/common/ReleaseNotesWidget";
import RouteAccessGuard from "@/components/common/RouteAccessGuard";
import SessionDebugInfo from "@/components/common/SessionDebugInfo";
import UserManagementModal from "@/components/common/UserManagementModal";
import NpsSurveyContainer from "@/components/nps/NpsSurveyContainer";
import { OnboardingModal } from "@/components/onboarding/OnboardingModal";
import { TooltipProvider } from "@/components/ui/tooltip";
import { normalizeLocaleLanguage, type SupportedLocaleLanguage } from "@/constants/localization";
import { GailDockProvider } from "@/contexts/chat/GailDockContext";
import { PageContextProvider } from "@/contexts/chat/PageContextContext";
import { WorkflowEditorBridgeProvider } from "@/contexts/chat/WorkflowEditorBridgeContext";
import { AuthProvider } from "@/contexts/common/AuthProvider";
import {ToolApprovalDecisionProvider} from "@/contexts/chat/ToolApprovalDecisionContext";
import MutationProposalPage from "@/components/chat/MutationProposalPage";
import MutationProposalReviewSlot from "@/components/chat/MutationProposalReviewSlot";
import { ChatTransferProvider } from "@/contexts/common/ChatTransferContextProvider";
import { NavContainerProvider } from "@/contexts/common/NavContainerProvider";
import { SessionTimeoutProvider } from "@/contexts/common/SessionTimeoutProvider";
import { UserOrganizationsProvider } from "@/contexts/common/UserOrganizationsProvider";
import { OnboardingModalProvider } from "@/contexts/onboarding/OnboardingModalContext";
import { THEME } from "@/enums/profile";
import { AlertContextProvider } from "@/hooks/useAlert";
import { BannerContextProvider } from "@/hooks/useBanner";
import { EodReportAlertProvider } from "@/hooks/useEodReportAlert";
import { UnsavedDataContextProvider } from "@/hooks/useHasUnsavedData";
import { OrgContextProvider } from "@/hooks/useOrg";
import { PermissionsContextProvider } from "@/hooks/usePermissions";
import { RestrictionsContextProvider } from "@/hooks/useRestrictions";
import { TrialUsageProvider } from "@/hooks/useTrialUsage";
import { UserProfileContextProvider } from "@/hooks/useUserProfile";
import { createAppI18nInstance } from "@/i18n/createAppI18n";
import type { LocaleResources } from "@/i18n/namespaces";
import type { AuthSeedResult } from "@/lib/auth-seed";
import { inter, sourceSerif } from "@/lib/fonts";
import { getBrandInlineScript } from "@/utils/common/brandInlineScript";
import { GetTheme } from "@/utils/common/general";
import { writeLanguageCookie } from "@/utils/common/languageCookie";
import {
  getPreferenceCookieMigrationScript,
  resolveDomTheme,
  writeThemeCookie,
} from "@/utils/common/themeCookie";
import { AppProgressProvider } from "@bprogress/next";
import { SpeedInsights } from "@vercel/speed-insights/next";
import { OverfadeProvider } from "./providers";
import ReleaseNoteInitialization from "./releaseNotesInitialization";

// Normalize arbitrary locale strings (e.g. "en-US" from navigator.language, or
// a potentially stale cached value) to a supported i18n language code. Falls
// back to English when the value is missing or unsupported.
const normalizeLanguage = (value: string | null | undefined): SupportedLocaleLanguage =>
  normalizeLocaleLanguage(value);

export default function CustomersClientLayout({
  children,
  authSeed,
  initialLanguage = "en",
  initialTheme = THEME.LIGHT,
  initialLocaleResources = null,
}: Readonly<{
  children: ReactNode;
  authSeed: AuthSeedResult;
  /** Cookie-derived language so SSR and the first client paint match. */
  initialLanguage?: SupportedLocaleLanguage | string;
  /** Cookie-derived theme preference so SSR and the first client paint match. */
  initialTheme?: THEME;
  /**
   * Server-loaded locale JSON for `initialLanguage` (non-English). Applied to
   * i18n before first paint so nav/copy does not flash English→translated.
   */
  initialLocaleResources?: LocaleResources | null;
}>) {
  const [show, setShow] = useState(false);
  // Per-tree i18n instance: never mutate the module singleton during SSR, or
  // concurrent requests can leak language into each other's renders.
  const [i18nInstance] = useState(() =>
    createAppI18nInstance(normalizeLanguage(initialLanguage), initialLocaleResources)
  );
  const [language, setLanguage] = useState(() => normalizeLanguage(initialLanguage));
  // Prefer cookie preference for the Select; resolve for `data-theme`.
  // `html` has suppressHydrationWarning so `auto` may resolve differently on
  // the client than SSR light — the head script applies the real value before paint.
  const [theme, setTheme] = useState<THEME.LIGHT | THEME.DARK>(() =>
    resolveDomTheme(initialTheme)
  );
  const [isHydrated, setIsHydrated] = useState(false);
  const isInitialSetupComplete = useRef(false);

  // Store i18n in a ref to avoid recreating handleLanguageChange
  const i18nRef = useRef(i18nInstance);
  i18nRef.current = i18nInstance;

  const handleLanguageChange = useCallback((newLanguage: string) => {
    const normalizedLanguage = normalizeLanguage(newLanguage);
    setLanguage(normalizedLanguage);
    writeLanguageCookie(normalizedLanguage);
    i18nRef.current.changeLanguage(normalizedLanguage);
  }, []); // No dependencies - stable callback

  // Hydration effect - runs once after initial render on client
  useEffect(() => {
    setIsHydrated(true);
    // Re-resolve in case preference is `auto` (SSR used light fallback).
    setTheme(resolveDomTheme(initialTheme));
  }, [initialTheme]);

  // Soft theme updates from useUserProfile (no full page reload).
  useEffect(() => {
    const onThemeChanged = (event: Event) => {
      const detail = (event as CustomEvent<{ theme?: THEME }>).detail;
      const preference = detail?.theme;
      if (preference) {
        writeThemeCookie(preference);
        setTheme(resolveDomTheme(preference));
        return;
      }
      setTheme(GetTheme() === THEME.DARK ? THEME.DARK : THEME.LIGHT);
    };
    window.addEventListener("themeChanged", onThemeChanged);
    return () => window.removeEventListener("themeChanged", onThemeChanged);
  }, []);

  // Ensure DOM attribute matches theme state after mount
  // Only update DOM after hydration to avoid hydration mismatch
  useEffect(() => {
    if (!isHydrated) return;

    // Always set the DOM attribute to match the theme state
    document.documentElement.dataset.theme = theme;

    // Mark initial setup as complete after DOM has been updated
    if (!isInitialSetupComplete.current) {
      // Use requestAnimationFrame to ensure DOM has been updated
      requestAnimationFrame(() => {
        isInitialSetupComplete.current = true;
      });
    }
  }, [theme, isHydrated]);

  // Protect against theme mutations from other sources
  const themeRef = useRef(theme);
  themeRef.current = theme;

  useEffect(() => {
    const targetNode = document.documentElement;

    const observer = new MutationObserver((mutationsList) => {
      // Don't interfere during initial setup
      if (!isInitialSetupComplete.current) {
        return;
      }

      for (const mutation of mutationsList) {
        if (mutation.type === "attributes" && mutation.attributeName === "data-theme") {
          const currentTheme = targetNode.dataset.theme;
          if (currentTheme !== themeRef.current) {
            targetNode.dataset.theme = themeRef.current;
          }
        }
      }
    });

    observer.observe(targetNode, { attributes: true });

    return () => {
      observer.disconnect();
    };
  }, []); // Observer created once — reads theme from ref

  const providers: ProviderConfig[] = useMemo(
    () => [
      {
        Component: AuthProvider,
        props: { authSeed },
      },
      SessionTimeoutProvider, // Must be after AuthProvider (uses auth state)
      EodReportAlertProvider, // Must be after AuthProvider (uses auth state)
      UserOrganizationsProvider,
      AlertContextProvider,
      OrgContextProvider,
      {
        Component: UserProfileContextProvider,
        props: {
          setLanguage: handleLanguageChange,
          initialLanguage: normalizeLanguage(initialLanguage),
          initialTheme,
        },
      },
      PermissionsContextProvider,
      {
        Component: AppProgressProvider,
        props: {
          color: "#0062E2",
          shallowRouting: true,
        },
      },
      // TrialUsageProvider must wrap NavContainerProvider — the sidebar
      // (rendered inside NavContainerProvider) hosts the TrialUsageWidget
      // and needs this context to be an ancestor, not a descendant.
      TrialUsageProvider,
      // GailDockProvider must wrap NavContainerProvider — the nav sidebars
      // (rendered inside NavContainerProvider) host the dock toggle item and
      // need this context to be an ancestor. The dock UI itself mounts
      // further down (GailDockLayout around the routed page).
      GailDockProvider,
      WorkflowEditorBridgeProvider,
      PageContextProvider,
      {
        Component: NavContainerProvider,
        props: {
          setShowUserManagement: setShow,
        },
      },
      {
        Component: BannerContextProvider,
      },
      UnsavedDataContextProvider,
      RestrictionsContextProvider,
      ChatTransferProvider,
      ToolApprovalDecisionProvider,
      TooltipProvider,
      OnboardingModalProvider,
    ],
    [handleLanguageChange, authSeed, initialLanguage, initialTheme]
  ); // setShow is stable, no need to include

  return (
    <html
      lang={language}
      translate="no"
      data-theme={theme}
      suppressHydrationWarning
      style={{ backgroundColor: "#333354" }}
      className={`${inter.variable} ${sourceSerif.variable}`}
    >
      <head>
        <script
          suppressHydrationWarning
          dangerouslySetInnerHTML={{ __html: getPreferenceCookieMigrationScript() }}
        />
        <script
          suppressHydrationWarning
          dangerouslySetInnerHTML={{ __html: getBrandInlineScript(true) }}
        />
        <GoogleTagManagerHead />
        <meta name="google" content="notranslate" />
        <link rel="icon" href="/favicon.ico" />
      </head>
      <body>
        <I18nextProvider i18n={i18nInstance}>
          <ReleaseNoteInitialization />
          <LayoutBodyScripts />
          <SpeedInsights />
          <ProviderComposer providers={providers}>
            <>
              <NotAuthorizedAlert />
              <RouteAccessGuard />
              <BrandSyncFromOrg />
              <OverfadeProvider />
              <ReleaseNotesWidget />
              <FeatureAnnouncementHost />
              <SupportWidget />
              <SessionDebugInfo />
              <UserManagementModal show={show} setShow={setShow} />
              <OnboardingModal />
              <NpsSurveyContainer />
              <LanguageMismatchNotice />
              {/* Assistant dock seam: the routed page renders beside the
                  summonable GailGPT dock and squeezes when it opens. */}
              <MutationProposalPage>
                <GailDockLayout>
                  <MutationProposalReviewSlot>{children}</MutationProposalReviewSlot>
                </GailDockLayout>
              </MutationProposalPage>
            </>
          </ProviderComposer>
        </I18nextProvider>
      </body>
    </html>
  );
}
